import { AnimatePresence, motion } from 'framer-motion';
import { Controller } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { useAnalytics } from 'use-analytics';
import { ChatGptAppButton } from '@/Domain/components/Auth/ChatGptAppButton';
import { FieldMessage } from '@/Domain/components/Auth/FieldMessage';
import { ReCaptchaCheckbox } from '@/Domain/components/Auth/ReCaptchaCheckbox';
import { ReCaptchaV3 } from '@/Domain/components/Auth/ReCaptchaV3';
import { GoogleButton } from '@/Domain/components/Auth/SocialButton/GoogleButton';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { FieldError } from '@/Domain/components/Shared/Field/Field';
import { Input } from '@/Domain/components/Shared/Input/Input';
import { PasswordInput } from '@/Domain/components/Shared/Input/PasswordInput';
import { EnvelopIcon } from '@/Domain/components/icons/Envelop.icon';
import LockLineIcon from '@/Domain/components/icons/LockLineIcon';
import { SocialPlatformEnum } from '@/Domain/enum/SocialPlatform.enum';
import { useRegister } from '@/Domain/hooks/Auth/useRegister.hook';
import { useSocialRegister } from '@/Domain/hooks/Auth/useSocialRegister.hook';
import { PASSWORD_MIN_LENGTH } from '@/Domain/utils/passwordPolicy';

export const RegisterPage = () => {
  const { t } = useTranslation('register');
  const { track } = useAnalytics();
  const { search } = useLocation();

  const {
    control,
    escalateToV2,
    handleV2Focus,
    shouldFocusV2,
    useV2,
    register,
    handleSubmit,
    onSubmit,
    onInvalidSubmit,
    errors,
    onVerify,
    watch,
    isPending,
  } = useRegister();
  const { handleSocialRegister, isNavigating } = useSocialRegister();

  const passwordLength = watch('password1')?.length ?? 0;
  const passwordHint =
    passwordLength < PASSWORD_MIN_LENGTH
      ? t('form.password_hint', { min: PASSWORD_MIN_LENGTH })
      : undefined;

  const handleTrack = () =>
    track('register_back_click', {
      event_category: 'User Registration',
      event_label: 'register_form',
    });

  return (
    <div className="animate-in fade-in duration-300">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="mb-2 text-2xl font-semibold text-foreground">{t('title')}</h1>
        <p className="text-sm text-muted-foreground">{t('subtitle')}</p>
      </div>

      <div className="grid gap-5">
        {/* Social Login */}
        <div className="grid gap-4">
          <GoogleButton
            onClick={(event) => handleSocialRegister(event, SocialPlatformEnum.Google)}
            disabled={isNavigating}
            isLoading={isNavigating}
          />
          <ChatGptAppButton source="register_page" />
        </div>

        {/* Divider */}
        <div className="relative flex pt-1 items-center">
          <div className="flex-grow border-t border-border"></div>
          <span className="mx-4 flex-shrink text-xs font-medium uppercase tracking-wide text-foreground-subtle">
            {t('or')}
          </span>
          <div className="flex-grow border-t border-border"></div>
        </div>

        {/* Register Form */}
        <form
          id="register-email-form"
          className="flex flex-col gap-5"
          onSubmit={handleSubmit(onSubmit, onInvalidSubmit)}
        >
          <ReCaptchaV3 onVerify={onVerify} onUnavailable={escalateToV2} />

          {/* Email Field */}
          <div className="space-y-1.5">
            <label htmlFor="email" className="text-sm font-medium text-foreground">
              {t('form.email')}
            </label>
            <Input
              id="email"
              size="lg"
              data-testid="register-email"
              {...register('username')}
              placeholder={t('form.email_placeholder')}
              error={!!errors.username}
              aria-invalid={!!errors.username}
              aria-describedby="email-message"
              autoFocus
              autoComplete="email"
              startIcon={<EnvelopIcon className="h-4 w-4 text-foreground-subtle" />}
            />
            <FieldMessage id="email-message" error={errors.username?.message} />
          </div>

          {/* Password Field */}
          <div className="space-y-1.5">
            <label htmlFor="password1" className="text-sm font-medium text-foreground">
              {t('form.password')}
            </label>
            <PasswordInput
              id="password1"
              size="lg"
              data-testid="register-password"
              placeholder={t('form.password_placeholder')}
              {...register('password1')}
              error={!!errors.password1}
              aria-invalid={!!errors.password1}
              aria-describedby="password1-message"
              autoComplete="new-password"
              startIcon={<LockLineIcon className="h-4 w-4 text-foreground-subtle" />}
            />
            <FieldMessage
              id="password1-message"
              error={errors.password1?.message}
              hint={passwordHint}
            />
          </div>

          {/* Confirm Password Field */}
          <div className="space-y-1.5">
            <label htmlFor="password2" className="text-sm font-medium text-foreground">
              {t('form.confirm_password')}
            </label>
            <PasswordInput
              id="password2"
              size="lg"
              data-testid="register-password-confirm"
              placeholder={t('form.password_placeholder')}
              {...register('password2')}
              error={!!errors.password2}
              aria-invalid={!!errors.password2}
              aria-describedby="password2-message"
              autoComplete="new-password"
              startIcon={<LockLineIcon className="h-4 w-4 text-foreground-subtle" />}
            />
            <FieldMessage id="password2-message" error={errors.password2?.message} />
          </div>

          <AnimatePresence initial={false}>
            {errors.root && (
              <motion.div
                key="root-error"
                // negative marginTop (-20px) cancels the form's gap-5 while collapsed, so the
                // banner expands/collapses smoothly with no instant gap jump before the button
                initial={{ opacity: 0, height: 0, marginTop: -20 }}
                animate={{ opacity: 1, height: 'auto', marginTop: 0 }}
                exit={{ opacity: 0, height: 0, marginTop: -20 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
                className="overflow-hidden"
              >
                <div
                  role="alert"
                  className="rounded-lg border border-destructive-muted bg-destructive-muted p-3"
                >
                  <p className="text-sm font-medium text-destructive">{errors.root.message}</p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* reCAPTCHA v2 Fallback */}
          {useV2 && (
            <Controller
              name="recaptcha_response_v2"
              control={control}
              render={({ field: { onChange, value }, fieldState: { invalid } }) => (
                <ReCaptchaCheckbox
                  onChange={onChange}
                  onFocusHandled={handleV2Focus}
                  value={value}
                  isInvalid={invalid}
                  shouldFocus={shouldFocusV2}
                />
              )}
            />
          )}
          {errors.recaptcha_response_v2 && (
            <FieldError className="text-center" data-testid="register-captcha-error">
              {t('validation:required')}
            </FieldError>
          )}

          {/* Submit Button */}
          <Button
            variant="dark"
            size="lg"
            type="submit"
            loading={isPending}
            className="w-full mt-1"
            data-testid="register-submit"
          >
            {t('button.submit')}
          </Button>
        </form>

        {/* Terms of Service */}
        <p className="text-center text-sm text-muted-foreground">
          <Trans
            i18nKey="register:terms_of_use"
            components={{
              terms_link: (
                <Link
                  className="font-medium text-foreground transition-colors hover-underline"
                  to="https://www.quickchat.ai/terms"
                  target="_blank"
                />
              ),
              privacy_link: (
                <Link
                  className="font-medium text-foreground transition-colors hover-underline"
                  to="https://www.quickchat.ai/privacy"
                  target="_blank"
                />
              ),
            }}
          />
        </p>

        {/* Login Link */}
        <div className="border-t border-border pt-4 text-center">
          <p className="text-sm text-muted-foreground">
            {t('have_account')}{' '}
            <Link
              className="font-medium text-foreground transition-colors hover-underline"
              to={`/${search}`}
              onClick={handleTrack}
            >
              {t('link.login')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
